import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { fetchGooglePlacesReviews } from "@/lib/googleReviews";

/**
 * GoogleReviewsContext — single source of truth for the boutique's Google
 * rating, total count, and review snippets across the site. Flow:
 *   1. On mount, GET /api/reviews/google (server-cached snapshot).
 *   2. If the snapshot is `stale` or missing AND we have a placeId + key,
 *      fetch fresh from Places JS in the browser and POST it back so other
 *      visitors read from cache.
 *   3. Exposes `refresh({ force })` for the Reviews page and the admin
 *      "Refresh Google reviews now" button.
 */

const GoogleReviewsContext = createContext(null);

const PLACES_API_KEY = process.env.REACT_APP_GOOGLE_PLACES_API_KEY || process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
const PLACES_PLACE_ID = process.env.REACT_APP_GOOGLE_PLACES_PLACE_ID || "";

export function GoogleReviewsProvider({ children }) {
  const [data, setData] = useState(null);        // last-known snapshot (server or fresh)
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const inFlight = useRef(false);

  const pushToBackend = useCallback(async (fresh) => {
    try {
      await api.post("/reviews/google/cache", {
        rating: fresh.rating,
        total: fresh.total,
        reviews: fresh.reviews,
        photos: fresh.photos || [],
      });
    } catch (_e) { /* cache is best-effort */ }
  }, []);

  const refresh = useCallback(async ({ force = false } = {}) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setRefreshing(true);
    try {
      // 1) Read the server-cached snapshot FIRST and paint it immediately —
      // this is what makes the homepage feel instant. The browser doesn't
      // block on the Places API at all.
      let server;
      try {
        const { data: s } = await api.get("/reviews/google");
        server = s;
        setData(s);
      } catch (_e) {
        server = null;
      }
      // Flip loading off as soon as we have (or fail to get) the cached
      // snapshot. Any subsequent Places JS fetch runs in the background
      // and won't hold the "Loading Google reviews…" placeholder open.
      setLoading(false);

      // 2) If the cache is stale, empty, or the caller forced a sync, kick
      // off a live Places JS fetch in the background and push the fresh
      // data back to the server cache so every other visitor benefits.
      const placeId = PLACES_PLACE_ID || server?.place_id;
      const needsFresh = force || !server || server.stale || (server.total === 0 && server.configured !== false);
      if (!needsFresh || !PLACES_API_KEY || !placeId) return;

      const fresh = await fetchGooglePlacesReviews({ apiKey: PLACES_API_KEY, placeId });
      if (!fresh) return;
      setData((prev) => ({
        ...(prev || {}),
        rating: fresh.rating,
        total: fresh.total,
        reviews: fresh.reviews,
        photos: fresh.photos || [],
        stale: false,
        cached_at: new Date().toISOString(),
        configured: true,
        place_id: placeId,
      }));
      pushToBackend(fresh);
    } finally {
      inFlight.current = false;
      setRefreshing(false);
    }
  }, [pushToBackend]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <GoogleReviewsContext.Provider value={{ data, loading, refreshing, refresh }}>
      {children}
    </GoogleReviewsContext.Provider>
  );
}

export function useGoogleReviews() {
  const ctx = useContext(GoogleReviewsContext);
  if (!ctx) throw new Error("useGoogleReviews must be used inside GoogleReviewsProvider");
  return ctx;
}
